import { useState, useEffect } from 'react';

/**
 * Hook to get current window size
 * Returns width and height, and updates on window resize
 */
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  return windowSize;
};

/**
 * Hook to get responsive drawer/modal width based on screen size
 * @param {string} defaultWidth - Default width from config (e.g., '800px', '50%', '600px')
 * @returns {string} Responsive width value
 */
export const useResponsiveWidth = (defaultWidth = '800px') => {
  const { width } = useWindowSize();

  // Parse the default width to handle both px and percentage values
  const parseWidth = (widthStr) => {
    if (!widthStr) return { value: 800, unit: 'px' };
    
    // Check if it's a percentage
    if (widthStr.includes('%')) {
      const percent = parseFloat(widthStr);
      return { value: percent, unit: '%', isPercent: true };
    }
    
    // Check if it's viewport width
    if (widthStr.includes('vw')) {
      const vw = parseFloat(widthStr);
      return { value: vw, unit: 'vw', isVW: true };
    }
    
    // Default to pixels
    const px = parseFloat(widthStr) || 800;
    return { value: px, unit: 'px' };
  };

  const parsed = parseWidth(defaultWidth);

  // If it's already a percentage or vw, use it as-is (it's already responsive)
  if (parsed.isPercent || parsed.isVW) {
    return defaultWidth;
  }

  // For pixel values, make them responsive based on screen size
  if (width < 640) {
    // Mobile: Full width with small margins
    return '95vw';
  } else if (width < 768) {
    // Small tablets: 90% width
    return '90vw';
  } else if (width < 1024) {
    // Tablets: 85% width
    return '85vw';
  } else if (width <= 1280) {
    // Small desktops and 1024px screens: Use 70vw for better fit
    // This ensures forms don't take up too much space on 1024x768 screens
    // and provides better visual balance
    return '70vw';
  } else if (width < 1536) {
    // Medium desktops: Use configured width but max 65vw
    const maxWidth = Math.min(parsed.value, window.innerWidth * 0.65);
    return `${maxWidth}px`;
  } else {
    // Large desktops: Use configured width
    return defaultWidth;
  }
};

